/**
 * gameSounds.js - Web Audio API sound effects for gamified experiences
 * 
 * No external files needed - generates sounds programmatically!
 * Inspired by retro games and modern UI feedback sounds.
 */

// Audio context singleton
let audioContext = null;

const getAudioContext = () => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  // Resume if suspended (browser autoplay policy)
  if (audioContext.state === 'suspended') {
    audioContext.resume();
  }
  return audioContext;
};

/**
 * Play a simple tone
 */
const playTone = (frequency, duration, type = 'sine', volume = 0.3) => {
  try {
    const ctx = getAudioContext();
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.frequency.value = frequency;
    oscillator.type = type;
    
    gainNode.gain.setValueAtTime(volume, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration);
    
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + duration);
  } catch (e) {
    console.log('Audio not available:', e);
  }
};

/**
 * Play a sequence of tones
 */
const playSequence = (notes, tempo = 150) => {
  notes.forEach((note, index) => {
    setTimeout(() => {
      playTone(note.freq, note.duration || 0.15, note.type || 'sine', note.volume || 0.3);
    }, index * tempo);
  });
};

// ============================================
// GAME SOUND EFFECTS
// ============================================

/**
 * Click/Select sound - Quick pop
 */
export const playClick = () => {
  playTone(800, 0.08, 'sine', 0.2);
};

/**
 * Button hover sound - Subtle
 */
export const playHover = () => {
  playTone(600, 0.05, 'sine', 0.1);
};

/**
 * Step complete / Success - Upward arpeggio
 */
export const playSuccess = () => {
  playSequence([
    { freq: 523, duration: 0.1 },  // C5
    { freq: 659, duration: 0.1 },  // E5
    { freq: 784, duration: 0.15 }, // G5
    { freq: 1047, duration: 0.2 }, // C6
  ], 80);
};

/**
 * Level up sound - Triumphant
 */
export const playLevelUp = () => {
  playSequence([
    { freq: 392, duration: 0.1, type: 'square', volume: 0.2 },  // G4
    { freq: 523, duration: 0.1, type: 'square', volume: 0.2 },  // C5
    { freq: 659, duration: 0.1, type: 'square', volume: 0.2 },  // E5
    { freq: 784, duration: 0.15, type: 'square', volume: 0.25 }, // G5
    { freq: 1047, duration: 0.3, type: 'square', volume: 0.3 }, // C6
  ], 100);
};

/**
 * Whoosh / Transition sound
 */
export const playWhoosh = () => {
  try {
    const ctx = getAudioContext();
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(400, ctx.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(100, ctx.currentTime + 0.15);
    
    gainNode.gain.setValueAtTime(0.15, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
    
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.15);
  } catch (e) {
    console.log('Audio not available:', e);
  }
};

/**
 * Error / Wrong choice sound
 */
export const playError = () => {
  playSequence([
    { freq: 200, duration: 0.15, type: 'sawtooth', volume: 0.2 },
    { freq: 150, duration: 0.2, type: 'sawtooth', volume: 0.2 },
  ], 100);
};

/**
 * Celebration / Completion fanfare
 */
export const playCelebration = () => {
  // Victory fanfare!
  playSequence([
    { freq: 523, duration: 0.12, type: 'square', volume: 0.25 },  // C5
    { freq: 523, duration: 0.12, type: 'square', volume: 0.25 },  // C5
    { freq: 523, duration: 0.12, type: 'square', volume: 0.25 },  // C5
    { freq: 523, duration: 0.3, type: 'square', volume: 0.25 },   // C5 (hold)
    { freq: 415, duration: 0.3, type: 'square', volume: 0.25 },   // Ab4
    { freq: 466, duration: 0.3, type: 'square', volume: 0.25 },   // Bb4
    { freq: 523, duration: 0.15, type: 'square', volume: 0.25 },  // C5
    { freq: 466, duration: 0.1, type: 'square', volume: 0.2 },    // Bb4
    { freq: 523, duration: 0.5, type: 'square', volume: 0.3 },    // C5 (final)
  ], 120);
};

/**
 * Coin / Points earned sound
 */
export const playCoin = () => {
  playSequence([
    { freq: 988, duration: 0.08, type: 'square', volume: 0.2 },  // B5
    { freq: 1319, duration: 0.15, type: 'square', volume: 0.25 }, // E6
  ], 60);
};

/**
 * Power up sound
 */
export const playPowerUp = () => {
  try {
    const ctx = getAudioContext();
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(300, ctx.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(1200, ctx.currentTime + 0.3);
    
    gainNode.gain.setValueAtTime(0.25, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.35);
    
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.35);
  } catch (e) {
    console.log('Audio not available:', e);
  }
};

/**
 * Start/Begin sound - Energetic
 */
export const playStart = () => {
  playSequence([
    { freq: 262, duration: 0.1, type: 'square', volume: 0.2 },  // C4
    { freq: 330, duration: 0.1, type: 'square', volume: 0.2 },  // E4
    { freq: 392, duration: 0.1, type: 'square', volume: 0.25 }, // G4
    { freq: 523, duration: 0.2, type: 'square', volume: 0.3 },  // C5
  ], 70);
};

/**
 * Notification / Alert sound
 */
export const playNotification = () => {
  playSequence([
    { freq: 880, duration: 0.1, volume: 0.2 },
    { freq: 880, duration: 0.1, volume: 0.2 },
  ], 150);
};

// Export all sounds as a single object too
export const gameSounds = {
  click: playClick,
  hover: playHover,
  success: playSuccess,
  levelUp: playLevelUp,
  whoosh: playWhoosh,
  error: playError,
  celebration: playCelebration,
  coin: playCoin,
  powerUp: playPowerUp,
  start: playStart,
  notification: playNotification,
};

export default gameSounds;
